import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Sparkles } from "lucide-react";
import { useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact "complementary products" strip.
 *
 * Props:
 *  - productId: anchor product (returns related items via /api/products/{id}/related)
 *  - title: section title (default: "You may also like")
 *  - onItemClick: optional callback when a related item is clicked
 */
export default function RelatedProducts({ productId, title = "You may also like", onItemClick }) {
  const { addToCart } = useCart();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/products/${productId}/related?limit=8`)
      .then((res) => {
        if (!cancelled) setItems(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);

  if (loading) {
    return (
      <div data-testid="related-products-loading" className="py-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-emerald-600" />
          <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} className="bg-slate-100 rounded-xl aspect-square animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div data-testid="related-products-section" className="py-4 border-t border-slate-200">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-4 w-4 text-emerald-600" />
        <h4 className="text-sm font-semibold text-slate-900">{title}</h4>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        {items.map((p) => {
          const price = p.is_special && p.special_price ? p.special_price : p.price;
          return (
            <div
              key={p.id}
              className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-emerald-400 hover:shadow-sm transition-all"
              data-testid={`related-product-${p.id}`}
            >
              <div
                className="relative aspect-square overflow-hidden bg-slate-100 cursor-pointer"
                onClick={() => onItemClick?.(p)}
              >
                {p.image_url ? (
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">
                    No image
                  </div>
                )}
              </div>
              <div className="p-2">
                <h5 className="text-xs font-medium text-slate-900 line-clamp-2 leading-tight min-h-[2rem]">
                  {p.name}
                </h5>
                <div className="flex items-center justify-between mt-2 gap-1">
                  <span className="text-sm font-bold text-emerald-600">£{price.toFixed(2)}</span>
                  <Button
                    size="sm"
                    onClick={() => {
                      addToCart(p, 1, p.weight_options?.[0] || null);
                      toast.success(`${p.name} added to cart!`);
                    }}
                    className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`related-add-${p.id}`}
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
